<template>
  <div class="position-tracker-floating">
    <!-- Permission Dialog Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showPermissionDialog" class="permission-modal-overlay">
          <div class="permission-modal">
            <div class="modal-icon">
              <svg viewBox="0 0 24 24" width="48" height="48">
                <path
                  d="M12,2A8,8 0 0,1 20,10C20,14.4 12,22 12,22C12,22 4,14.4 4,10A8,8 0 0,1 12,2M12,4A6,6 0 0,0 6,10C6,12 12,18.7 12,18.7C12,18.7 18,12 18,10A6,6 0 0,0 12,4M12,7A3,3 0 0,1 15,10A3,3 0 0,1 12,13A3,3 0 0,1 9,10A3,3 0 0,1 12,7Z"
                  fill="#667eea"
                />
              </svg>
            </div>

            <h2 class="modal-title">{{ currentStep.title }}</h2>
            <p class="modal-description">{{ currentStep.description }}</p>

            <div v-if="currentStep.benefits" class="benefits-list">
              <div
                v-for="benefit in currentStep.benefits"
                :key="benefit"
                class="benefit-item"
              >
                <svg class="benefit-icon" viewBox="0 0 20 20" width="20" height="20">
                  <path
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    fill="#10b981"
                  />
                </svg>
                <span>{{ benefit }}</span>
              </div>
            </div>

            <div class="modal-actions">
              <button
                v-if="!currentStep.required"
                @click="skipPermission"
                class="btn-secondary"
              >
                {{ currentStep.skipText || 'Skip' }}
              </button>
              <button
                @click="handlePermissionAction"
                class="btn-primary"
                :disabled="isProcessingPermission"
              >
                {{ isProcessingPermission ? 'Processing...' : currentStep.actionText }}
              </button>
            </div>

            <div v-if="permissionError" class="permission-error">
              <p>{{ permissionError }}</p>
              <div class="error-actions">
                <button @click="openSettings" class="settings-link">
                  Open Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Floating action button -->
    <button
      class="tracker-fab"
      :class="fabClasses"
      :title="buttonTitle"
      @click="handleFabClick"
      :disabled="isWebPlatform"
    >
      <Transition name="icon-rotate" mode="out-in">
        <svg
          v-if="isWebPlatform"
          class="marker-icon"
          viewBox="0 0 24 24"
          width="24"
          height="24"
        >
          <path
            d="M12,2A8,8 0 0,1 20,10C20,14.4 12,22 12,22C12,22 4,14.4 4,10A8,8 0 0,1 12,2M12,4A6,6 0 0,0 6,10C6,12 12,18.7 12,18.7C12,18.7 18,12 18,10A6,6 0 0,0 12,4M15,7H9V9H15V7Z"
            fill="currentColor"
          />
        </svg>

        <svg
          v-else-if="running"
          class="marker-icon"
          viewBox="0 0 24 24"
          width="24"
          height="24"
        >
          <path
            d="M12,2C15.31,2 18,4.66 18,7.95C18,12.41 12,19 12,19C12,19 6,12.41 6,7.95C6,4.66 8.69,2 12,2M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6"
            fill="currentColor"
          />
        </svg>

        <svg
          v-else-if="permBlocked"
          class="marker-icon"
          viewBox="0 0 24 24"
          width="24"
          height="24"
        >
          <path
            d="M12,2A8,8 0 0,1 20,10C20,14.4 12,22 12,22C12,22 4,14.4 4,10A8,8 0 0,1 12,2M12,4A6,6 0 0,0 6,10C6,12 12,18.7 12,18.7C12,18.7 18,12 18,10A6,6 0 0,0 12,4M11,15H13V17H11V15M11,7H13V13H11V7Z"
            fill="currentColor"
          />
        </svg>

        <svg v-else class="marker-icon" viewBox="0 0 24 24" width="24" height="24">
          <path
            d="M12,2A8,8 0 0,1 20,10C20,14.4 12,22 12,22C12,22 4,14.4 4,10A8,8 0 0,1 12,2M12,4A6,6 0 0,0 6,10C6,12 12,18.7 12,18.7C12,18.7 18,12 18,10A6,6 0 0,0 12,4M12,7A3,3 0 0,1 15,10A3,3 0 0,1 12,13A3,3 0 0,1 9,10A3,3 0 0,1 12,7Z"
            fill="currentColor"
          />
        </svg>
      </Transition>

      <div class="ripple-container">
        <div v-if="running" class="ripple"></div>
      </div>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { registerPlugin, Capacitor } from '@capacitor/core'
import { useOAuthService } from '@/services/OAuthService'
import AnalyticsController from '@/controllers/AnalyticsController'

const PositionTracker = registerPlugin('PositionTracker')
const { getUserProfile } = useOAuthService()

const CONFIG = {
  useBatch: true,
  requestBackground: true,
  flushMs: 2500,
  maxBatch: 12,
  minDistM: 0.6,
  maxAccM: 120,
  teleportM: 60
}

const running = ref(false)
const permBlocked = ref(false)
const isWebPlatform = ref(false)
const showPermissionDialog = ref(false)
const currentPermissionStep = ref(0)
const isProcessingPermission = ref(false)
const permissionError = ref('')
const uploadQueue = ref([])
const flushTimer = ref(null)
const backoffDelay = ref(3000)
const permissionSteps = ref([])
const hasInitialCheck = ref(false)

const ANDROID_PERMISSION_STEPS = [
  {
    id: 'core',
    title: 'Location Permission',
    description: 'Grant access to device location for position tracking',
    method: 'requestCore',
    required: true,
    actionText: 'Grant Location',
    benefits: ['GPS positioning', 'Network location', 'Essential for tracking']
  },
  {
    id: 'bluetooth-enable',
    title: 'Enable Bluetooth',
    description: 'Turn on Bluetooth for indoor positioning (stays in app)',
    method: 'requestBluetoothEnable',
    required: true,
    actionText: 'Enable Bluetooth',
    isBluetoothEnable: true,
    benefits: ['One-tap enable', 'No need to leave app', 'Indoor beacon detection']
  },
  {
    id: 'bluetooth',
    title: 'Bluetooth Scanning Permission',
    description: 'Allow scanning for nearby beacons and WiFi networks',
    method: 'requestBluetooth',
    required: true,
    actionText: 'Grant Bluetooth',
    benefits: ['Scan for beacons', '2-5m indoor accuracy', 'Room-level tracking']
  },
  {
    id: 'bodySensors',
    title: 'Motion Sensors',
    description: 'Access accelerometer and gyroscope for movement detection',
    method: 'requestBodySensors',
    required: true,
    actionText: 'Grant Sensors',
    benefits: ['Motion detection', 'Step counting', 'Movement tracking']
  },
  {
    id: 'activityRecognition',
    title: 'Activity Recognition',
    description: 'Detect walking, running, and stationary states',
    method: 'requestActivityRecognition',
    required: true,
    actionText: 'Grant Activity',
    benefits: ['Movement type detection', 'Better filtering', 'Improved accuracy']
  },
  {
    id: 'notifications',
    title: 'Enable Notifications',
    description: 'Get updates about tracking status and accuracy',
    method: 'requestNotifications',
    required: false,
    actionText: 'Enable Notifications',
    skipText: 'Skip',
    benefits: ['Status updates', 'Position alerts', 'Monitor accuracy']
  },
  {
    id: 'location-system',
    title: 'Enable GPS',
    description: 'Turn on GPS in system settings (you\'ll leave the app briefly)',
    method: 'openLocationSettings',
    required: true,
    actionText: 'Open GPS Settings',
    isSystemSetting: true,
    settingType: 'location',
    benefits: ['Enable GPS', 'Network location', 'Required for tracking']
  },
  {
    id: 'wifi-system',
    title: 'Enable WiFi',
    description: 'Turn on WiFi in system settings (you\'ll leave the app briefly)',
    method: 'openWifiSettings',
    required: true,
    actionText: 'Open WiFi Settings',
    isSystemSetting: true,
    settingType: 'wifi',
    benefits: ['WiFi positioning', 'Indoor accuracy', 'Network-based location']
  },
  {
    id: 'background',
    title: 'Background Location',
    description: 'Continue tracking when app is minimized',
    method: 'requestBackground',
    required: false,
    actionText: 'Allow Background',
    skipText: 'Skip for Now',
    benefits: ['Continuous tracking', 'Background operation', 'Complete history']
  }
]

const currentStep = computed(() => {
  return permissionSteps.value[currentPermissionStep.value] || permissionSteps.value[0]
})

const buttonTitle = computed(() => {
  if (isWebPlatform.value) return 'Not available on web'
  if (running.value) return 'Stop tracking'
  if (permBlocked.value) return 'Fix permissions'
  return 'Start tracking'
})

const fabClasses = computed(() => ({
  'is-tracking': running.value,
  'has-error': permBlocked.value,
  'is-disabled': isWebPlatform.value
}))

let lastLat = null
let lastLon = null
let totalReceived = 0
let totalSent = 0
let positionListener = null
let statusListener = null

async function initializeConfig() {
  try {
    await getUserProfile()
  } catch (e) {
    console.error('PositionTracker: Config init failed', e)
  }
}

function detectPlatform() {
  isWebPlatform.value = !Capacitor.isNativePlatform()
}

async function checkAllPermissions() {
  try {
    const perms = await PositionTracker.checkPermissions()
    const settings = await PositionTracker.checkSystemSettings()

    return {
      location: perms?.location === 'granted',
      bluetooth: perms?.bluetooth === 'granted',
      bodySensors: perms?.bodySensors === 'granted',
      activityRecognition: perms?.activityRecognition === 'granted',
      background: perms?.background === 'granted',
      notifications: perms?.notifications === 'granted',
      locationEnabled: settings?.locationEnabled === true,
      bluetoothEnabled: settings?.bluetoothEnabled === true,
      wifiEnabled: settings?.wifiEnabled === true
    }
  } catch (e) {
    console.error('PositionTracker: Permission check failed', e)
    return {
      location: false,
      bluetooth: false,
      bodySensors: false,
      activityRecognition: false,
      background: false,
      notifications: false,
      locationEnabled: false,
      bluetoothEnabled: false,
      wifiEnabled: false
    }
  }
}

async function checkRequiredPermissions() {
  const status = await checkAllPermissions()
  return (
    status.locationEnabled &&
    status.bluetoothEnabled &&
    status.wifiEnabled &&
    status.location &&
    status.bluetooth &&
    status.bodySensors &&
    status.activityRecognition
  )
}

async function buildPermissionSteps() {
  const status = await checkAllPermissions()
  const steps = []

  if (!status.location) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'core'))
  }
  if (!status.bluetoothEnabled) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'bluetooth-enable'))
  }
  if (!status.bluetooth) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'bluetooth'))
  }
  if (!status.bodySensors) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'bodySensors'))
  }
  if (!status.activityRecognition) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'activityRecognition'))
  }
  if (!status.notifications) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'notifications'))
  }
  if (!status.locationEnabled) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'location-system'))
  }
  if (!status.wifiEnabled) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'wifi-system'))
  }
  if (!status.background && CONFIG.requestBackground) {
    steps.push(ANDROID_PERMISSION_STEPS.find(s => s.id === 'background'))
  }

  return steps.filter(s => s)
}

async function startPermissionFlow() {
  permissionSteps.value = await buildPermissionSteps()

  if (permissionSteps.value.length === 0) {
    await start()
    return
  }

  currentPermissionStep.value = 0
  permissionError.value = ''
  permBlocked.value = false
  showPermissionDialog.value = true
}

async function handlePermissionAction() {
  const step = currentStep.value
  if (!step) return

  isProcessingPermission.value = true
  permissionError.value = ''

  try {
    // Handle Bluetooth enable (in-app dialog)
    if (step.isBluetoothEnable) {
      const settings = await PositionTracker.checkSystemSettings()
      
      if (settings?.bluetoothEnabled) {
        moveToNextStep()
        isProcessingPermission.value = false
        return
      }
      
      try {
        await PositionTracker.requestBluetoothEnable()
        
        setTimeout(async () => {
          const updatedSettings = await PositionTracker.checkSystemSettings()
          
          if (updatedSettings?.bluetoothEnabled) {
            moveToNextStep()
          } else {
            await PositionTracker.openBluetoothSettings()
            permissionError.value = 'Please enable Bluetooth and return to the app'
          }
          isProcessingPermission.value = false
        }, 1000)
      } catch (e) {
        console.log('PositionTracker: In-app Bluetooth enable failed, opening settings')
        await PositionTracker.openBluetoothSettings()
        permissionError.value = 'Please enable Bluetooth and return to the app'
        isProcessingPermission.value = false
      }
      return
    }

    // Handle system settings (leaves app)
    if (step.isSystemSetting) {
      await PositionTracker[step.method]()

      setTimeout(async () => {
        const settings = await PositionTracker.checkSystemSettings()

        if (step.settingType === 'location' && settings?.locationEnabled) {
          moveToNextStep()
        } else if (step.settingType === 'wifi' && settings?.wifiEnabled) {
          moveToNextStep()
        } else {
          permissionError.value = `Please enable ${step.settingType} and return to the app`
        }
        isProcessingPermission.value = false
      }, 1000)
      return
    }

    // Handle regular permissions (in-app) - with smooth fallback for location
    try {
      await PositionTracker[step.method]()
      await new Promise(resolve => setTimeout(resolve, 500))

      const status = await checkAllPermissions()
      let granted = false

      switch (step.id) {
        case 'core': granted = status.location; break
        case 'bluetooth': granted = status.bluetooth; break
        case 'bodySensors': granted = status.bodySensors; break
        case 'activityRecognition': granted = status.activityRecognition; break
        case 'background': granted = status.background; break
        case 'notifications': granted = status.notifications; break
      }

      if (granted) {
        moveToNextStep()
      } else if (step.required) {
        // For location permission, open location settings directly
        if (step.id === 'core') {
          await PositionTracker.openLocationSettings()
          permissionError.value = 'Please grant location permission and return to the app'
        } else {
          permissionError.value = 'Permission denied. Open settings below to grant manually.'
          permBlocked.value = true
        }
      } else {
        moveToNextStep()
      }
    } catch (e) {
      console.error('PositionTracker: Permission request failed', e)
      
      // Smooth fallback for location
      if (step.id === 'core' && step.required) {
        await PositionTracker.openLocationSettings()
        permissionError.value = 'Please grant location permission and return to the app'
      } else if (step.required) {
        permissionError.value = 'Permission denied. Open settings below to grant manually.'
        permBlocked.value = true
      } else {
        moveToNextStep()
      }
    }
  } catch (e) {
    console.error('PositionTracker: Permission action failed', e)
    
    // Fallback for Bluetooth and location
    if (step.isBluetoothEnable) {
      await PositionTracker.openBluetoothSettings()
      permissionError.value = 'Please enable Bluetooth and return to the app'
    } else if (step.id === 'core') {
      await PositionTracker.openLocationSettings()
      permissionError.value = 'Please grant location permission and return to the app'
    } else if (step.required) {
      permissionError.value = 'An error occurred. Please use settings below.'
      permBlocked.value = true
    } else {
      moveToNextStep()
    }
  } finally {
    if (!step.isSystemSetting && !step.isBluetoothEnable) {
      isProcessingPermission.value = false
    }
  }
}

function skipPermission() {
  const step = currentStep.value
  if (!step.required) {
    moveToNextStep()
  }
}

async function moveToNextStep() {
  currentPermissionStep.value++
  permissionError.value = ''

  if (currentPermissionStep.value >= permissionSteps.value.length) {
    showPermissionDialog.value = false

    const hasRequired = await checkRequiredPermissions()
    if (hasRequired) {
      await start()
    } else {
      permBlocked.value = true
    }
  }
}

function openSettings() {
  const step = currentStep.value

  try {
    if (step.isBluetoothEnable) {
      PositionTracker.openBluetoothSettings()
      return
    }
    
    if (step.isSystemSetting) {
      if (step.settingType === 'location') {
        PositionTracker.openLocationSettings()
      } else if (step.settingType === 'wifi') {
        PositionTracker.openWifiSettings()
      }
      return
    }
    
    if (step.id === 'core' || step.id === 'background') {
      PositionTracker.openLocationSettings()
    } else if (step.id === 'bluetooth') {
      PositionTracker.openBluetoothSettings()
    } else {
      PositionTracker.openAppSettings()
    }
  } catch (e) {
    console.error('PositionTracker: Failed to open settings', e)
  }
}

async function start() {
  if (running.value || isWebPlatform.value) return

  const hasRequired = await checkRequiredPermissions()
  if (!hasRequired) {
    await startPermissionFlow()
    return
  }

  try {
    await PositionTracker.start()
    running.value = true
    permBlocked.value = false
    console.log('PositionTracker: Started')
  } catch (e) {
    console.error('PositionTracker: Start failed', e)
    permBlocked.value = true
  }
}

async function stop() {
  if (!running.value) return

  try {
    await PositionTracker.stop()
    running.value = false
    console.log('PositionTracker: Stopped')
  } catch (e) {
    console.error('PositionTracker: Stop failed', e)
  }
}

async function handleFabClick() {
  if (isWebPlatform.value) return

  if (running.value) {
    await stop()
  } else {
    const hasRequired = await checkRequiredPermissions()
    if (!hasRequired) {
      await startPermissionFlow()
    } else {
      await start()
    }
  }
}

function distanceMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) ** 2
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function shouldEnqueuePoint(point) {
  if (!point.lat || !point.lon) return false
  if (point.accuracy > CONFIG.maxAccM) return false

  if (lastLat && lastLon) {
    const dist = distanceMeters(lastLat, lastLon, point.lat, point.lon)
    const minDist = point.accuracy < 10 ? CONFIG.minDistM * 0.5 : CONFIG.minDistM
    if (dist < minDist) return false
    if (dist > CONFIG.teleportM && point.accuracy > 20) return false
  }

  return true
}

function enqueuePoint(point) {
  totalReceived++
  if (!shouldEnqueuePoint(point)) return

  lastLat = point.lat
  lastLon = point.lon

  uploadQueue.value.push({
    ts: point.ts ?? Date.now(),
    lat: point.lat,
    lon: point.lon,
    accuracy: point.accuracy,
    speed: point.speed ?? 0,
    altitude: point.altitude ?? null,
    heading: point.heading ?? null,
    isMoving: point.isMoving ?? null,
    source: point.source || 'unknown',
    motionConfidence: point.motionConfidence ?? 0,
    num_satellites_used: point.num_satellites_used ?? 0,
    floorIndex: point.floorIndex ?? 0,
    isLocked: point.isLocked ?? false,
    isStationary: point.isStationary ?? false,
    meta: { app: 'PIA', source: 'vue-android', version: '3.0' }
  })

  if (uploadQueue.value.length >= CONFIG.maxBatch) {
    flushQueue()
  } else {
    ensureFlushTimer()
  }
}

function ensureFlushTimer() {
  if (flushTimer.value) return

  flushTimer.value = setTimeout(() => {
    flushTimer.value = null
    flushQueue()
  }, CONFIG.flushMs)
}

async function flushQueue() {
  if (!uploadQueue.value.length) return

  const batch = uploadQueue.value.splice(0, CONFIG.maxBatch)
  totalSent += batch.length

  try {
    const normalized = batch.map(b => ({
      ts: b.ts,
      lat: b.lat,
      lon: b.lon,
      accuracy: b.accuracy,
      speed: b.speed,
      altitude: b.altitude,
      heading: b.heading,
      is_moving: b.isMoving
    }))

    const result = await AnalyticsController.postLocationsBatch(normalized)
    if (!result?.ok) throw new Error('batch failed')

    backoffDelay.value = 3000
    if (uploadQueue.value.length) ensureFlushTimer()
  } catch (e) {
    uploadQueue.value.unshift(...batch)
    setTimeout(ensureFlushTimer, backoffDelay.value)
    backoffDelay.value = Math.min(backoffDelay.value * 2, 60000)
    console.error('PositionTracker: Upload failed', e)
  }
}

function onPositionUpdate(payload) {
  try {
    enqueuePoint(payload)
  } catch (e) {
    console.error('PositionTracker: Position error', e)
  }
}

function onStatusUpdate(statusData) {
  try {
    running.value = !!statusData?.running
  } catch (e) {
    console.error('PositionTracker: Status error', e)
  }
}

async function checkAndAutoStart() {
  await nextTick()
  await new Promise(resolve => setTimeout(resolve, 500))

  const hasRequired = await checkRequiredPermissions()
  if (hasRequired) {
    console.log('PositionTracker: Auto-starting')
    await start()
  }
}

onMounted(async () => {
  await initializeConfig()
  detectPlatform()

  if (isWebPlatform.value) return

  try {
    positionListener = await PositionTracker.addListener('position', onPositionUpdate)
    statusListener = await PositionTracker.addListener('status', onStatusUpdate)

    if (!hasInitialCheck.value) {
      hasInitialCheck.value = true
      await checkAndAutoStart()
    }
  } catch (e) {
    console.error('PositionTracker: Init failed', e)
  }
})

onBeforeUnmount(() => {
  try {
    positionListener?.remove?.()
    statusListener?.remove?.()
    if (flushTimer.value) clearTimeout(flushTimer.value)
    if (uploadQueue.value.length > 0) flushQueue()
  } catch (e) {
    console.error('PositionTracker: Cleanup error', e)
  }
})
</script>
<style scoped>
.position-tracker-floating {
  position: fixed;
  right: 65px;
  bottom: 260px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
}

.permission-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  pointer-events: auto;
}

.permission-modal {
  background: white;
  border-radius: 24px;
  padding: 32px;
  max-width: 440px;
  width: 100%;
  box-shadow:
    0 24px 48px rgba(0, 0, 0, 0.12),
    0 12px 24px rgba(0, 0, 0, 0.08);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
  text-align: center;
}

.modal-description {
  font-size: 15px;
  line-height: 1.6;
  color: #6b7280;
  margin: 0 0 24px 0;
  text-align: center;
}

.benefits-list {
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.benefit-item:last-child {
  margin-bottom: 0;
}

.benefit-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.benefit-item span {
  font-size: 14px;
  line-height: 1.5;
  color: #4b5563;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  max-width: 240px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: transparent;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.permission-error {
  margin-top: 20px;
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  text-align: center;
}

.permission-error p {
  color: #991b1b;
  font-size: 14px;
  margin: 0 0 12px 0;
}

.error-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.settings-link {
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 12px;
  text-decoration: underline;
  transition: color 0.2s ease;
  border-radius: 6px;
}

.settings-link:hover {
  color: #2563eb;
  background: rgba(59, 130, 246, 0.1);
}

.tracker-fab {
  pointer-events: auto;
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  outline: none;
  cursor: pointer;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 12px 40px rgba(102, 126, 234, 0.25),
    0 4px 16px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
}

.tracker-fab::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.tracker-fab:hover:not(.is-disabled) {
  transform: translateY(-4px) scale(1.08);
  box-shadow:
    0 20px 50px rgba(102, 126, 234, 0.35),
    0 8px 24px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.tracker-fab:hover:not(.is-disabled)::before {
  opacity: 1;
}

.tracker-fab:active:not(.is-disabled) {
  transform: translateY(-2px) scale(1.02);
  transition: all 0.15s ease;
}

.tracker-fab.is-tracking {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow:
    0 12px 40px rgba(16, 185, 129, 0.25),
    0 4px 16px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.tracker-fab.is-tracking:hover {
  box-shadow:
    0 20px 50px rgba(16, 185, 129, 0.35),
    0 8px 24px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.tracker-fab.has-error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow:
    0 12px 40px rgba(239, 68, 68, 0.25),
    0 4px 16px rgba(0, 0,
    0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.tracker-fab.has-error:hover:not(.is-disabled) {
  box-shadow:
    0 20px 50px rgba(239, 68, 68, 0.35),
    0 8px 24px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.tracker-fab.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
}

.marker-icon {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  color: #ffffff;
}

.ripple-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  overflow: hidden;
  pointer-events: none;
}

.ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  animation: ripple-animation 2s infinite;
}

@keyframes ripple-animation {
  0% {
    width: 0;
    height: 0;
    opacity: 1;
  }
  100% {
    width: 140px;
    height: 140px;
    opacity: 0;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.icon-rotate-enter-active,
.icon-rotate-leave-active {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.icon-rotate-enter-from {
  opacity: 0;
  transform: rotate(180deg) scale(0.6);
}

.icon-rotate-leave-to {
  opacity: 0;
  transform: rotate(-180deg) scale(0.6);
}

@media (max-width: 960px) {
  .position-tracker-floating {
    right: 35px;
    bottom: 260px;
  }

  .tracker-fab {
    width: 60px;
    height: 60px;
  }

  .marker-icon {
    width: 22px;
    height: 22px;
  }

  .permission-modal {
    padding: 24px;
    max-width: 380px;
  }
}

@media (max-width: 768px) {
  .position-tracker-floating {
    right: 25px;
    bottom: 240px;
  }

  .tracker-fab {
    width: 56px;
    height: 56px;
  }

  .marker-icon {
    width: 20px;
    height: 20px;
  }

  .permission-modal {
    padding: 20px;
    max-width: calc(100% - 40px);
  }

  .modal-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .position-tracker-floating {
    right: 20px;
    bottom: 220px;
  }

  .tracker-fab {
    width: 52px;
    height: 52px;
  }

  .marker-icon {
    width: 18px;
    height: 18px;
  }

  .permission-modal {
    padding: 20px;
    border-radius: 20px;
  }

  .modal-title {
    font-size: 20px;
  }

  .modal-description {
    font-size: 14px;
  }
}

@media (prefers-color-scheme: dark) {
  .permission-modal {
    background: #1f2937;
    color: #e5e7eb;
  }

  .modal-title {
    color: #f3f4f6;
  }

  .modal-description {
    color: #d1d5db;
  }

  .benefits-list {
    background: #111827;
  }

  .benefit-item span {
    color: #9ca3af;
  }

  .btn-secondary {
    color: #d1d5db;
    border-color: #374151;
  }

  .btn-secondary:hover {
    background: #1f2937;
    border-color: #4b5563;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tracker-fab,
  .modal-fade-enter-active,
  .modal-fade-leave-active,
  .icon-rotate-enter-active,
  .icon-rotate-leave-active {
    animation: none !important;
    transition: none !important;
  }

  .ripple {
    display: none;
  }
}

@media (prefers-contrast: high) {
  .tracker-fab,
  .permission-modal,
  .btn-primary,
  .btn-secondary {
    border: 2px solid currentColor;
  }
}
</style>